import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { LTInputBox, LTText } from '@lambdatestincprivate/lt-components';
import { prioritizeRows, usePackState, useTestRow } from '../../data/pack-store';
import { InstanceRow } from './InstanceRow';
import { instanceMatches } from './search';
import styles from './instances.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var ROW_ESTIMATE_PX = 69;
var OVERSCAN = 8;
var PRIORITIZE_DEBOUNCE_MS = 50;
var SEARCH_DEBOUNCE_MS = 180;
function VirtualRow(_ref) {
  var id = _ref.id,
    onSelect = _ref.onSelect;
  var row = useTestRow(id);
  if (!row) return null;
  return /*#__PURE__*/_jsx(InstanceRow, {
    row: row,
    onSelect: onSelect
  });
}

/**
 * "All Test Instances" — every test in the pack, virtualized. Rows render as
 * skeletons the moment the central directory is parsed and fill in place as
 * their result.yaml hydrates; scrolling reprioritizes hydration so the rows
 * on screen always load first.
 *
 * Search is a debounced, in-memory filter (title + testcase id) over the store's
 * rows — no network; the virtualizer renders only visible matches, so even
 * very large packs filter in milliseconds.
 */
export function InstanceList(_ref2) {
  var scrollElement = _ref2.scrollElement,
    query = _ref2.query,
    onQueryChange = _ref2.onQueryChange,
    onSelect = _ref2.onSelect;
  var rowIds = usePackState(function (s) {
    return s.rowIds;
  });
  var rows = usePackState(function (s) {
    return s.rows;
  });
  var progress = usePackState(function (s) {
    return s.progress;
  });
  var setQuery = onQueryChange;
  var _useState = useState(query),
    _useState2 = _slicedToArray(_useState, 2),
    debouncedQuery = _useState2[0],
    setDebouncedQuery = _useState2[1];
  useEffect(function () {
    var t = setTimeout(function () {
      return setDebouncedQuery(query);
    }, SEARCH_DEBOUNCE_MS);
    return function () {
      return clearTimeout(t);
    };
  }, [query]);
  var searching = debouncedQuery.trim() !== '';
  var filteredIds = useMemo(function () {
    if (!searching) return rowIds;
    return rowIds.filter(function (id) {
      return instanceMatches(rows.get(id), id, debouncedQuery);
    });
  }, [rowIds, rows, debouncedQuery, searching]);
  var virtualizer = useVirtualizer({
    count: filteredIds.length,
    getScrollElement: function getScrollElement() {
      return scrollElement;
    },
    estimateSize: function estimateSize() {
      return ROW_ESTIMATE_PX;
    },
    overscan: OVERSCAN
  });

  // A new filter changes what "row 0" is — start the list from the top.
  useEffect(function () {
    if (scrollElement) scrollElement.scrollTop = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);
  var items = virtualizer.getVirtualItems();
  var firstIndex = items.length > 0 ? items[0].index : -1;
  var lastIndex = items.length > 0 ? items[items.length - 1].index : -1;

  // Tell the scheduler which rows are (near-)visible — debounced so a fast
  // fling doesn't spam reprioritization. Uses the FILTERED ids, so search
  // results hydrate first.
  var debounceRef = useRef();
  useEffect(function () {
    if (firstIndex < 0) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(function () {
      prioritizeRows(filteredIds.slice(firstIndex, lastIndex + 1));
    }, PRIORITIZE_DEBOUNCE_MS);
    return function () {
      return clearTimeout(debounceRef.current);
    };
  }, [firstIndex, lastIndex, filteredIds]);
  var loading = progress.done + progress.failed < progress.total;
  return /*#__PURE__*/_jsxs("section", {
    className: styles.section,
    "aria-busy": loading,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.toolbar,
      children: [/*#__PURE__*/_jsx(LTText, {
        as: "h2",
        text: "All Test Instances",
        variant: LTText.TextVariant.SUBHEADER_BOLD,
        color: "var(--lt-text-primary)",
        className: styles.heading
      }), rowIds.length > 0 && /*#__PURE__*/_jsxs("div", {
        className: styles.searchRow,
        children: [/*#__PURE__*/_jsx("div", {
          className: styles.searchBox,
          children: /*#__PURE__*/_jsx(LTInputBox, {
            type: "text",
            name: "instance-search",
            ariaLabel: "Search test instances",
            placeholder: "Search by name or ID",
            size: LTInputBox.Size.MEDIUM,
            variant: LTInputBox.Variant.WHITE,
            value: query,
            onChange: function onChange(value) {
              return setQuery(value);
            }
            // Built-in clear only resets internal state — mirror it.
            ,
            trailingAction: function trailingAction() {
              return setQuery('');
            }
          })
        }), searching && /*#__PURE__*/_jsx(LTText, {
          text: "".concat(filteredIds.length, " of ").concat(rowIds.length, " instances").concat(loading ? ' — still loading' : ''),
          variant: LTText.TextVariant.SMALL_REGULAR,
          color: "var(--lt-text-secondary)"
        })]
      })]
    }), /*#__PURE__*/_jsx("span", {
      className: "ev-sr-only",
      "aria-live": "polite",
      children: searching ? "".concat(filteredIds.length, " of ").concat(rowIds.length, " instances match") : ''
    }), rowIds.length === 0 ? /*#__PURE__*/_jsx(LTText, {
      as: "p",
      text: "This pack contains no tests.",
      variant: LTText.TextVariant.SUBHEADER_REGULAR,
      color: "var(--lt-text-secondary)",
      className: styles.empty
    }) : filteredIds.length === 0 ? /*#__PURE__*/_jsxs("div", {
      className: styles.noMatch,
      children: [/*#__PURE__*/_jsx(LTText, {
        text: "No instances match \"".concat(debouncedQuery.trim(), "\""),
        variant: LTText.TextVariant.SUBHEADER_REGULAR,
        color: "var(--lt-text-secondary)"
      }), /*#__PURE__*/_jsx("button", {
        type: "button",
        className: styles.clearSearch,
        onClick: function onClick() {
          return setQuery('');
        },
        children: "Clear search"
      })]
    }) : /*#__PURE__*/_jsx("div", {
      className: styles.listFrame,
      children: /*#__PURE__*/_jsx("ul", {
        className: styles.list,
        style: {
          height: virtualizer.getTotalSize()
        },
        children: items.map(function (item) {
          return /*#__PURE__*/_jsx("li", {
            ref: virtualizer.measureElement,
            "data-index": item.index
            // Virtualization mounts only ~visible rows — without an
            // explicit set size/position a screen reader announces
            // "list, 17 items" for a 1000-test run.
            ,
            "aria-setsize": filteredIds.length,
            "aria-posinset": item.index + 1,
            className: "".concat(styles.listItem, " ").concat(item.index === filteredIds.length - 1 ? styles.listItemLast : ''),
            style: {
              transform: "translateY(".concat(item.start, "px)")
            },
            children: /*#__PURE__*/_jsx(VirtualRow, {
              id: filteredIds[item.index],
              onSelect: onSelect
            })
          }, filteredIds[item.index]);
        })
      })
    })]
  });
}